import { Bone, Group, Object3D, Quaternion, Vector3 } from 'three'

/**
 * Serializable description of a custom skeleton authored in Build Skeleton mode.
 *
 * Persisted to IndexedDB and bundled into ProjectArchive .json exports. The
 * downstream pipeline (EditSkeleton → BindPose → AnimationsListing) only
 * understands a Three.js Object3D armature with Bone children, so this
 * descriptor exists purely as a JSON-friendly intermediate that
 * `materialize()` converts into the same shape the GLB loader produces.
 */

export const SKELETON_DESCRIPTOR_FORMAT = 'meshinator-skeleton'
export const SKELETON_DESCRIPTOR_VERSION = 1

export type Vec3 = [number, number, number]
export type QuatXYZW = [number, number, number, number]

export interface BoneDescriptor {
  name: string
  parent_index: number | null
  head: Vec3
  tail: Vec3
  rotation_quaternion: QuatXYZW
  /** When true, the bone is treated as immutable in Build Skeleton: the
   *  transform-controls gizmo won't attach, and rename/delete are blocked.
   *  Optional for backwards-compatibility with v1 descriptors authored before
   *  this field existed; missing means unlocked. */
  locked?: boolean
  /** When > 0, marks the bone as an inverse-kinematics target. Dragging the
   *  bone in the editor bends the previous N parent bones (CCD solver) so
   *  that the bone reaches the dragged world position. 0 or undefined means
   *  the bone is a normal forward-kinematic bone. */
  ik_chain_length?: number
  /** Optional world-space direction (need not be a unit vector — solver
   *  normalizes). When set on an IK target, the solver runs a final
   *  alignment pass that rotates the chain around the root→end-effector
   *  axis so the chain's bend plane aligns with the pole. Resolves the
   *  ambiguity of which way an "elbow" should point. */
  pole_vector?: Vec3
  /** Swing-twist rotation limits in degrees. The local quaternion is
   *  decomposed into a twist around `twist_axis` and a swing perpendicular
   *  to it; each component is independently clamped. Avoids the gimbal-lock
   *  coupling that XYZ Euler limits suffer near ±90°. Hinge joints (e.g.,
   *  elbows) are expressed as `swing.max_deg = 0` plus a constrained twist
   *  range. Applied both inside the IK solver and during manual rotate-
   *  gizmo edits (live during drag). */
  rotation_limits?: {
    twist_axis?: 'x' | 'y' | 'z' // default 'y' (Blender convention)
    twist?: { min_deg: number, max_deg: number }
    swing?: { max_deg: number }
  }
  /** Visual shape used for the bone's head handle in the Build Skeleton
   *  editor. Purely cosmetic — does not affect skinning, animation, or any
   *  downstream pipeline. Useful for marking control bones (rings),
   *  IK targets (octahedrons), translation controls (cubes), etc. Default
   *  (or undefined) is 'sphere'. */
  display_shape?: BoneDisplayShape
}

export type BoneDisplayShape = 'sphere' | 'cube' | 'octahedron' | 'pyramid' | 'ring' | 'arrow'

export const BONE_DISPLAY_SHAPES: readonly BoneDisplayShape[] = [
  'sphere', 'cube', 'octahedron', 'pyramid', 'ring', 'arrow'
] as const

export interface SkeletonDescriptor {
  format: typeof SKELETON_DESCRIPTOR_FORMAT
  version: typeof SKELETON_DESCRIPTOR_VERSION
  id: string
  name: string
  created_at: string
  updated_at: string
  bones: BoneDescriptor[]
  /** Optional preview image (data URL) captured when the user saved this
   *  skeleton from the editor. Used by the library list to give a visual cue.
   *  Optional for back-compat — descriptors authored before this field
   *  existed have no thumbnail. */
  thumbnail_data_url?: string
}

export interface MaterializedSkeleton {
  armature: Group
  bones: Bone[]
}

export function generate_skeleton_id (): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for environments without crypto.randomUUID — sufficient for an
  // app-local identifier; not used for security purposes.
  let out = ''
  for (let i = 0; i < 32; i++) {
    out += Math.floor(Math.random() * 16).toString(16)
  }
  return out
}

export function create_empty_descriptor (name: string = 'Untitled Skeleton'): SkeletonDescriptor {
  const now = new Date().toISOString()
  return {
    format: SKELETON_DESCRIPTOR_FORMAT,
    version: SKELETON_DESCRIPTOR_VERSION,
    id: generate_skeleton_id(),
    name,
    created_at: now,
    updated_at: now,
    bones: []
  }
}

/**
 * Convert a SkeletonDescriptor into a Three.js armature Object3D whose children
 * are THREE.Bone instances parented per `parent_index`. The shape matches what
 * StepLoadSkeleton produces from a GLB: a top-level Object3D with Bone children
 * (and grandchildren, etc.) at local positions and rest-pose rotations.
 */
export function materialize_skeleton (descriptor: SkeletonDescriptor): MaterializedSkeleton {
  const armature = new Group()
  armature.name = descriptor.name === '' ? 'Loaded Armature' : descriptor.name

  const bones: Bone[] = []
  for (const bone_desc of descriptor.bones) {
    const bone = new Bone()
    bone.name = bone_desc.name
    bone.position.set(bone_desc.head[0], bone_desc.head[1], bone_desc.head[2])
    bone.quaternion.set(
      bone_desc.rotation_quaternion[0],
      bone_desc.rotation_quaternion[1],
      bone_desc.rotation_quaternion[2],
      bone_desc.rotation_quaternion[3]
    )
    bones.push(bone)
  }

  for (let i = 0; i < descriptor.bones.length; i++) {
    const parent_index = descriptor.bones[i].parent_index
    if (parent_index === null) {
      armature.add(bones[i])
    } else {
      bones[parent_index].add(bones[i])
    }
  }

  armature.updateMatrixWorld(true)
  return { armature, bones }
}

/**
 * Inverse of `materialize_skeleton` — walks an existing Three.js armature and
 * extracts a SkeletonDescriptor. Stub for a future "import skeleton from GLB"
 * flow; not exposed in UI yet but kept here so the round-trip exists for tests.
 *
 * Tail is defaulted to (0, 0.1, 0) per bone since it can't be recovered from a
 * generic armature without authoring metadata.
 */
export function descriptor_from_armature (armature: Object3D, name: string): SkeletonDescriptor {
  const bones: Bone[] = []
  armature.traverse((obj) => {
    if (obj instanceof Bone) bones.push(obj)
  })

  const bone_index = new Map<Bone, number>()
  bones.forEach((b, i) => bone_index.set(b, i))

  const bone_descriptors: BoneDescriptor[] = bones.map((bone) => {
    const parent = bone.parent
    const parent_index = parent instanceof Bone ? (bone_index.get(parent) ?? null) : null
    const pos = bone.position
    const q = bone.quaternion
    return {
      name: bone.name,
      parent_index,
      head: [pos.x, pos.y, pos.z] as Vec3,
      tail: [0, 0.1, 0] as Vec3,
      rotation_quaternion: [q.x, q.y, q.z, q.w] as QuatXYZW
    }
  })

  const now = new Date().toISOString()
  return {
    format: SKELETON_DESCRIPTOR_FORMAT,
    version: SKELETON_DESCRIPTOR_VERSION,
    id: generate_skeleton_id(),
    name,
    created_at: now,
    updated_at: now,
    bones: bone_descriptors
  }
}

/**
 * Compute the world-space tail position of a bone using the descriptor's local
 * tail offset and the parent chain. Useful for placing visual tail handles in
 * the viewport without having to re-walk the materialized scene each frame.
 */
export function compute_world_tail (
  descriptor: SkeletonDescriptor,
  bone_index: number,
  bones: Bone[]
): Vector3 {
  const bone = bones[bone_index]
  const local_tail = descriptor.bones[bone_index].tail
  const out = new Vector3(local_tail[0], local_tail[1], local_tail[2])
  bone.updateWorldMatrix(true, false)
  out.applyMatrix4(bone.matrixWorld)
  return out
}

/**
 * Validate a parsed unknown value as a SkeletonDescriptor. Returns the typed
 * descriptor on success, throws an Error with a user-readable message on
 * failure. Used by ProjectArchive when applying imported data and by storage
 * layer when reading from IDB (defending against schema drift).
 */
export function parse_skeleton_descriptor (raw: unknown): SkeletonDescriptor {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('Skeleton descriptor must be an object.')
  }
  const r = raw as Partial<SkeletonDescriptor>
  if (r.format !== SKELETON_DESCRIPTOR_FORMAT) {
    throw new Error('Skeleton descriptor has invalid format tag.')
  }
  if (r.version !== SKELETON_DESCRIPTOR_VERSION) {
    throw new Error(`Unsupported skeleton descriptor version: ${String(r.version)}.`)
  }
  if (typeof r.id !== 'string' || typeof r.name !== 'string') {
    throw new Error('Skeleton descriptor missing id or name.')
  }
  if (typeof r.created_at !== 'string' || typeof r.updated_at !== 'string') {
    throw new Error('Skeleton descriptor missing timestamps.')
  }
  if (!Array.isArray(r.bones)) {
    throw new Error('Skeleton descriptor bones must be an array.')
  }
  if (r.thumbnail_data_url !== undefined && typeof r.thumbnail_data_url !== 'string') {
    throw new Error('Skeleton descriptor thumbnail_data_url must be a string when present.')
  }
  for (const bone of r.bones) {
    if (
      typeof bone !== 'object' || bone === null ||
      typeof bone.name !== 'string' ||
      (bone.parent_index !== null && typeof bone.parent_index !== 'number') ||
      !is_vec3(bone.head) || !is_vec3(bone.tail) || !is_quat(bone.rotation_quaternion) ||
      (bone.locked !== undefined && typeof bone.locked !== 'boolean') ||
      (bone.ik_chain_length !== undefined && (typeof bone.ik_chain_length !== 'number' || bone.ik_chain_length < 0 || !Number.isFinite(bone.ik_chain_length))) ||
      (bone.pole_vector !== undefined && !is_vec3(bone.pole_vector)) ||
      (bone.rotation_limits !== undefined && !is_rotation_limits(bone.rotation_limits)) ||
      (bone.display_shape !== undefined && !is_bone_display_shape(bone.display_shape))
    ) {
      throw new Error('Skeleton descriptor contains an invalid bone entry.')
    }
  }
  return r as SkeletonDescriptor
}

function is_bone_display_shape (v: unknown): v is BoneDisplayShape {
  return typeof v === 'string' && (BONE_DISPLAY_SHAPES as readonly string[]).includes(v)
}

function is_rotation_limits (v: unknown): boolean {
  if (typeof v !== 'object' || v === null) return false
  const r = v as Record<string, unknown>
  if (r.twist_axis !== undefined && r.twist_axis !== 'x' && r.twist_axis !== 'y' && r.twist_axis !== 'z') return false
  if (r.twist !== undefined) {
    if (typeof r.twist !== 'object' || r.twist === null) return false
    const t = r.twist as Record<string, unknown>
    if (typeof t.min_deg !== 'number' || typeof t.max_deg !== 'number') return false
    if (!Number.isFinite(t.min_deg) || !Number.isFinite(t.max_deg)) return false
    if (t.min_deg > t.max_deg) return false
  }
  if (r.swing !== undefined) {
    if (typeof r.swing !== 'object' || r.swing === null) return false
    const s = r.swing as Record<string, unknown>
    if (typeof s.max_deg !== 'number' || !Number.isFinite(s.max_deg) || s.max_deg < 0) return false
  }
  return true
}

function is_vec3 (v: unknown): v is Vec3 {
  return Array.isArray(v) && v.length === 3 && v.every(n => typeof n === 'number' && Number.isFinite(n))
}

function is_quat (v: unknown): v is QuatXYZW {
  return Array.isArray(v) && v.length === 4 && v.every(n => typeof n === 'number' && Number.isFinite(n))
}

/**
 * Re-export Quaternion / Vector3 helpers as a tiny convenience for callers
 * dealing with `BoneDescriptor` math without pulling THREE directly.
 */
export const SkeletonMath = {
  vec3: (x: number, y: number, z: number): Vec3 => [x, y, z],
  identity_quat: (): QuatXYZW => [0, 0, 0, 1],
  quat_from_three: (q: Quaternion): QuatXYZW => [q.x, q.y, q.z, q.w],
  vec3_from_three: (v: Vector3): Vec3 => [v.x, v.y, v.z]
}
